import { API_BASE_URL } from "../../../config";
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import RowActionsMenu from '../../Components/RowActionsMenu';
import CustomerLink from '../../Components/CustomerLink';

import { fetchPurchaseRanking, trailing7Range, buildPurchaseMap, fetchRutineAll, buildRouteMap } from '../../Utils/customerListData';

const CustomerDataManagement = () => {
    const [customerDatas, setCustomerDatas] = useState([]);
    const [filteredLocations, setFilteredLocations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    // true when that call failed (not "no data") — the cell then says โหลดไม่สำเร็จ, not ไม่มีข้อมูล
    const [loadFailed, setLoadFailed] = useState({ avg: false, route: false });
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const navigate = useNavigate();

    const handleLogout = useCallback(() => {
        localStorage.removeItem('user');
        navigate('/login');
    }, [navigate]);

    const checkToken = useCallback(() => {
        const user = localStorage.getItem('user');
        if (!user) {
            // If your new API no longer requires authentication, you can return null here without logout
            // For now, keep previous behavior
            handleLogout();
            return null;
        }
        return JSON.parse(user)?.token;
    }, [handleLogout]);

    // purchaseMap/todaySet come from /saledata/customers/purchase-ranking — REGULAR-customers-only
    // scope (vector 2026-07-06 decision baked into that endpoint), so WALK_IN/AGENT rows always
    // get null/false here — there's no purchase-ranking data for those customer types. tankCountMap
    // is the same story — tanks only ever link to REGULAR (2026-07-21).
    const normalizeCustomers = (payload, purchaseMap = {}, todaySet = new Set(), routeMap = {}, tankCountMap = {}) => {
        const list = [];
        const regular = payload?.regular || [];
        const walkIn = payload?.walkIn || [];
        const agent = payload?.agent || [];
        const potential = payload?.potential || [];

        regular.forEach((c) => {
            const uid = c.customerUid || c.id || c.uid;
            const p = purchaseMap[uid];
            list.push({
                id: uid,
                type: 'REGULAR',
                storeName: c.storeName || c.customerName || 'ไม่ทราบชื่อ',
                latitude: c.latitude,
                longitude: c.longitude,
                customerAvailable: c.operating === undefined ? true : !!c.operating,
                avgBagsPerWeek: p ? p.bagsPerWeek : null,
                customerClass: p ? p.customerClass : null,
                purchaseToday: todaySet.has(uid),
                assignedSellers: routeMap[uid] || [],
                tankCount: tankCountMap[uid] ?? 0,
                raw: c,
            });
        });
        walkIn.forEach((c) => {
            const uid = c.walkInCustomerUid || c.id || c.uid;
            list.push({
                id: uid,
                type: 'WALK_IN',
                storeName: c.storeName || c.walkInCustomerName || 'ไม่ทราบชื่อ',
                latitude: c.latitude,
                longitude: c.longitude,
                customerAvailable: c.operating === undefined ? true : !!c.operating,
                avgBagsPerWeek: null,
                customerClass: null,
                purchaseToday: false,
                assignedSellers: routeMap[uid] || [],
                tankCount: null,
                raw: c,
            });
        });
        agent.forEach((c) => {
            const uid = c.agentCustomerUid || c.id || c.uid;
            list.push({
                id: uid,
                type: 'AGENT',
                storeName: c.storeName || c.agentName || 'ไม่ทราบชื่อ',
                latitude: c.latitude,
                longitude: c.longitude,
                customerAvailable: c.operating === undefined ? true : !!c.operating,
                avgBagsPerWeek: null,
                customerClass: null,
                purchaseToday: false,
                assignedSellers: routeMap[uid] || [],
                tankCount: null,
                raw: c,
            });
        });
        // POTENTIAL customers have no `operating` field at all (they're prospective — never in
        // the live sales system to begin with), so customerAvailable stays null → renders "-".
        potential.forEach((c) => {
            const uid = c.potentialCustomerUid || c.id || c.uid;
            list.push({
                id: uid,
                type: 'POTENTIAL',
                storeName: c.storeName || 'ไม่ทราบชื่อ',
                latitude: c.latitude,
                longitude: c.longitude,
                customerAvailable: null,
                avgBagsPerWeek: null,
                customerClass: null,
                purchaseToday: false,
                assignedSellers: routeMap[uid] || [],
                tankCount: null,
                raw: c,
            });
        });
        return list;
    };

    const fetchCustomerDatas = useCallback(async () => {
        const token = checkToken();
        // If token is required and missing, stop
        if (!token) return;

        try {
            // by-category, both purchase-ranking windows, and customer-buy-rutine/all are all
            // independent of each other — fire them together instead of stacking sequential
            // awaits, so the critical path is max(all four) instead of their sum. (This mattered:
            // stacked sequentially the page took 9+s even after shrinking the ranking window —
            // see the 7-day comment below.) seller-names still has to wait since it needs the
            // sellerIds rutine/all returns.
            const today = new Date();
            const [trailing7] = trailing7Range(today);
            const [response, avgRes, todayRes, rutineRes, tankCountMap] = await Promise.all([
                fetch(`${API_BASE_URL}/customer/api/customers/by-category`, {
                    method: 'POST',
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                    body: JSON.stringify({ type: 'ALL' }),
                }),
                // เฉลี่ยการซื้อ = trailing 7-day bagsPerWeek/customerClass (shared loader — the map
                // popup uses the same one; see Utils/customerListData for the 7-day rationale).
                fetchPurchaseRanking(token, trailing7, today),
                // ซื้อวันนี้ = same-day range, membership = bought today.
                fetchPurchaseRanking(token, today, today),
                // สายที่รับผิดชอบ — bulk reverse lookup on customer_buy_rutine (540 rows, one fetch).
                fetchRutineAll(token),
                // จำนวนถัง — one aggregate GROUP BY call (customerUid -> count), not a paginated
                // GET /api/tanks loop, to avoid regressing the 14s->9s load-time fix above.
                fetch(`${API_BASE_URL}/customer/api/tanks/count-by-customer`, {
                    headers: { Authorization: `Bearer ${token}` },
                }).then(r => r.ok ? r.json() : {}).catch(() => ({})),
            ]);

            if (!response.ok) {
                if (response.status === 403) {
                    handleLogout();
                    return;
                }
                const errorText = await response.text();
                setError(`Failed to fetch customers: ${errorText}`);
                throw new Error(`Failed to fetch customers: ${errorText}`);
            }

            const data = await response.json();

            setLoadFailed({ avg: avgRes === null, route: !Array.isArray(rutineRes) });
            const purchaseMap = buildPurchaseMap(avgRes);
            const todaySet = new Set((todayRes?.customers || []).map(e => e.customerId));

            // Resolve seller display names for the route map — depends on rutineRes above, so
            // this one call has to happen after (everything else was independent and parallel).
            const routeMap = await buildRouteMap(token, rutineRes);

            const normalized = normalizeCustomers(data, purchaseMap, todaySet, routeMap, tankCountMap || {});
            setCustomerDatas(normalized);
            setFilteredLocations(normalized);
        } catch (error) {
            setError('Error fetching customers');
            console.error('Fetch error:', error);
        } finally {
            setLoading(false);
        }
    }, [checkToken, handleLogout]);

    useEffect(() => {
        if (!selectedCustomer) {
            setFilteredLocations(customerDatas);
        } else {
            setFilteredLocations(
                customerDatas.filter((location) =>
                    (location.storeName || '').toLowerCase().includes((selectedCustomer.label || '').toLowerCase())
                )
            );
        }
    }, [selectedCustomer, customerDatas]);

    useEffect(() => {
        fetchCustomerDatas();
    }, [fetchCustomerDatas]);

    return (
        <div className="btc-container">
            <h1>หน้าต่างจัดการร้าน</h1>
            {loading ? (
                <p>Loading...</p>
            ) : (
                <>
                    {error && <p className="error">{error}</p>}

                    <div className="btc-search-bar" style={{ maxWidth: '20%' }}>
                        <Select
                            options={customerDatas.map((customer) => ({
                                value: customer.id,
                                label: customer.storeName,
                            }))}
                            onChange={setSelectedCustomer}
                            placeholder="ค้นหาชื่อร้าน..."
                            isClearable
                            value={selectedCustomer}
                            className="btc-selectBox-container"
                            classNamePrefix="btc-selectBox"
                        />
                    </div>

                    <table className="btc-table">
                        <thead>
                        <tr>
                            <th>ชื่อร้าน</th>
                            <th>เกรดลูกค้า</th>
                            <th>เฉลี่ยการซื้อ (7 วัน)</th>
                            <th>ซื้อวันนี้</th>
                            <th>แสดงในระบบการขาย</th>
                            <th>สายที่รับผิดชอบ</th>
                            <th>จำนวนถัง</th>
                            <th>ตัวเลือก</th>
                        </tr>
                        </thead>
                        <tbody>
                        {filteredLocations.map((customerData) => (
                            <tr key={customerData.id}>
                                <td><CustomerLink name={customerData.storeName} code={customerData.id} type={customerData.type} /></td>

                                <td>{customerData.customerClass || 'ไม่มีข้อมูล'}</td>

                                <td>
                                    {customerData.avgBagsPerWeek != null ? `${customerData.avgBagsPerWeek} ถุง/สัปดาห์` : (loadFailed.avg && customerData.type === 'REGULAR' ? 'โหลดไม่สำเร็จ' : 'ไม่มีข้อมูล')}
                                </td>

                                <td>{customerData.purchaseToday ? 'ซื้อ' : 'ไม่ซื้อ'}</td>
                                <td>{customerData.customerAvailable === null ? '-' : (customerData.customerAvailable ? 'แสดง' : 'ไม่แสดง')}</td>

                                <td>
                                    {customerData.assignedSellers?.length > 0 ? (
                                        customerData.assignedSellers.map((seller, index) => (
                                            <div key={index}>
                                                {seller.sellerName}
                                            </div>
                                        ))
                                    ) : (
                                        <div>{loadFailed.route ? 'โหลดไม่สำเร็จ' : 'ไม่มีผู้รับผิดชอบ'}</div>
                                    )}
                                </td>
                                <td>{customerData.tankCount != null ? `${customerData.tankCount} ถัง` : 'ไม่มีข้อมูล'}</td>
                                <td>
                                    <RowActionsMenu
                                        actions={[
                                            {
                                                label: 'แก้ไข',
                                                icon: <i className="fas fa-edit"></i>,
                                                onClick: () => navigate(`/customerData/edit/${customerData.id}`, { state: { customerData } }),
                                            },
                                            {
                                                label: 'รายละเอียด',
                                                icon: <i className="fas fa-info-circle"></i>,
                                                onClick: () => navigate(`/customerDetail/${customerData.type}/${customerData.id}`, { state: { customerData } }),
                                            },
                                            {
                                                label: 'แผนที่',
                                                icon: <i className="fas fa-map-marker-alt"></i>,
                                                onClick: () => {
                                                    const mapCenter = [customerData.latitude, customerData.longitude];
                                                    const zoom = 18;
                                                    localStorage.setItem('mapCenter', JSON.stringify(mapCenter));
                                                    localStorage.setItem('mapZoom', JSON.stringify(zoom));
                                                    navigate('/DashMap');
                                                },
                                            },
                                        ]}
                                    />
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </>
            )}
        </div>
    );
};

export default CustomerDataManagement;
